import React, { useState, useEffect, useRef } from 'react';
import { Checkbox, Row, Col, Typography, Descriptions, Modal, Form, Select, Button, Spin, Carousel, Card, Input, notification } from 'antd';
import { ReloadOutlined, SearchOutlined, ExclamationCircleOutlined, CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import * as userActions from '../../redux/actions/userActions';
import {
  formItemLayout,
  tailFormItemLayout,
  unexpectedError,
  onlyYears,
  onlyNumber,
  onlyNumbers,
  trimmedValues,
} from '../../common_functions';
import { PAPER_SHEEN, OBA, PAPER_WEIGHT, PAPER_TEXTURE } from '../../config';
const PaperGroup = props => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const [loading, setLoading] = useState(false);
  const [objectID, setObjectID] = useState(null);
  const [checkedGroup, setCheckedGroup] = useState(null);
  const [data, setData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paperGroups, setPaperGroups] = useState([]);
  const [paperGroupID, setPaperGroupID] = useState(null);

  const totalSlides = Math.ceil(paperGroups.length / 3);
  const handleBeforeChange = (from, to) => {
    if (to < totalSlides) {
      setCurrentIndex(to);
    }
  };
  const backButton = () => {
    props.onChange('', 'basic_photo', 'Basic Photos');
  };
  const goToList = () => {
    setTimeout(() => props.history.push('/print-objects', 1500));
  };
  const resetFilters = () => {
    setPaperGroups([]);
    formRef.current.resetFields();
  };

  useEffect(() => {
    setLoading(true);
    async function fetchData() {
      try {
        const objID = props?.objectData?.id;
        if (objID) {
          setObjectID(objID);
          let statsData = '';
          let { data } = await dispatch(
            userActions.getItem('collection_obj', objID, {
              fields: [
                'id,paper_group.id,paper_group.name,paper_group.paper_family.name,paper_group.ba_sr_ratio_low_limit,paper_group.ba_sr_ratio_high_limit',
                'print.id,print.creation_date_start_year,print.creation_date_end_year,print.stamp.period_end_date_year,print.stamp.period_start_date_year',
                'print.xrf.avg_dmin_ba,print.xrf.avg_dmin_sr,print.xrf.avg_dmin_ba_sd,print.xrf.avg_dmin_sr_sd',
                'print.xrf.avg_dmin_ba_sr,print.xrf.avg_dmin_ba_sr,print.xrf.presence_of_selenium_se,print.xrf.presence_of_silver_ag,print.xrf.dmin_sample_area.dmin_sample_area',
                'print.photo_spectro_meter.oba,print.micrometer.paper_weight,print.glossmeter.paper_sheen',
              ],
            })
          );
          if (data?.paper_group?.id) {
            setPaperGroupID(data?.paper_group?.id);
            setCheckedGroup(data?.paper_group?.id);
            const { data: stats } = await dispatch(
              userActions.getItems('paper_group_stats', {
                fields: ['id,print_date_earliest_early,print_date_latest_late'],
                filters: { paper_group: { _eq: data.paper_group.id } },
                sort: '-id',
                limit: 1,
              })
            );
            statsData = stats?.[0] || {};
          }
          const paperGroupData = data?.paper_group;
          const printData = data?.print;
          const printDate = onlyYears(printData?.creation_date_start_year, printData?.creation_date_end_year);
          const stampData = data?.print?.stamp;
          const stampDate = onlyYears(stampData?.period_start_date_year, stampData?.period_end_date_year);
          const xrf = data?.print?.xrf?.[0];
          const glossmeter = data?.print?.glossmeter?.[0];
          const micrometer = data?.print?.micrometer?.[0];
          const photoSpectroMeter = data?.print?.photo_spectro_meter?.[0];
          const groupStatPrintDate = onlyYears(statsData?.print_date_earliest_early, statsData?.print_date_latest_late);
          setData([
            { label: 'Absolute Ba', value: xrf?.avg_dmin_ba },
            { label: 'Absolute Sr', value: xrf?.avg_dmin_sr },
            { label: 'Ba/Sr Ratio Target', value: xrf?.avg_dmin_ba_sr },
            { label: 'Ba/Sr Ratio Low Limit', value: paperGroupData?.ba_sr_ratio_low_limit },
            { label: 'Ba/Sr Ratio High Limit', value: paperGroupData?.ba_sr_ratio_high_limit },
            { label: 'Optical Brightener', value: photoSpectroMeter?.oba },
            { label: 'Paper Sheen', value: glossmeter?.paper_sheen },
            { label: 'Paper Weight', value: micrometer?.paper_weight },
            { label: 'Paper Texture', value: 'No value' },
            { label: 'Se', value: xrf?.presence_of_selenium_se },
            { label: 'Dmin Type', value: xrf?.dmin_sample_area?.dmin_sample_area },
            { label: 'Print Date', value: printDate },
            { label: 'Stamp Date', value: stampDate },
            { label: 'Group Stat Print Date', value: groupStatPrintDate },
            { label: 'Name', value: paperGroupData?.name },
            { label: 'Paper Family', value: paperGroupData?.paper_family?.name },
          ]);
          setLoading(false);
        }
      } catch (error) {
        unexpectedError(error);
      }
    }

    if (props.activeTab === 'paper_group') {
      fetchData();
    }
  }, [props.activeTab]);
  const onFinish = async (values = {}) => {
    values = trimmedValues(values);
    const hasValues = Object.values(values).some(value => value);
    if (!hasValues) {
      notification['error']({
        message: 'No values provided, all fields are blank!',
        duration: 2,
      });
      formRef.current.resetFields();
      return;
    }
    if (!validateFields()) {
      return;
    }

    let condition = {};

    if (values?.ba_sr_ratio_avg_target_range && values?.ba_sr_ratio_average) {
      const rangeValue = parseFloat(values.ba_sr_ratio_avg_target_range); // Ensure it's a number
      const ratioTarget = parseFloat(values.ba_sr_ratio_average); // Ensure it's a number
      // Calculate lower and higher ranges
      const lowerRange = Math.max(0, ratioTarget - rangeValue);
      const higherRange = ratioTarget + rangeValue;
      condition = { _and: [{ ba_sr_ratio_average: { _gte: lowerRange } }, { ba_sr_ratio_average: { _lte: higherRange } }] };
    }
    // return;
    // const queryMapping = {
    //   optical_brightener: '_eq',
    //   ba_absolute_low: '_icontains',
    //   ba_absolute_high: '_icontains',
    //   sr_absolute_low: '_icontains',
    //   sr_absolute_high: '_icontains',
    //   ba_sr_ratio_average: '_icontains',
    //   ba_sr_ratio_low_limit: '_icontains',
    //   ba_sr_ratio_high_limit: '_icontains',
    //   paper_sheen: '_eq',
    //   paper_weight: '_eq',
    // };
    const queryMapping = {
      optical_brightener: '_eq',
      ba_absolute_low: '_eq',
      ba_absolute_high: '_eq',
      sr_absolute_low: '_eq',
      sr_absolute_high: '_eq',
      // ba_sr_ratio_average: condition,
      ba_sr_ratio_low_limit: '_eq',
      ba_sr_ratio_high_limit: '_eq',
      paper_sheen: '_eq',
      paper_weight: '_eq',
    };
    delete values.ba_sr_ratio_average;
    delete values.ba_sr_ratio_avg_target_range;

    // const filter = {
    //   _and: Object.entries(values).reduce((acc, [key, value]) => {
    //     if (value !== undefined && value !== '') {
    //       acc.push({ [key]: { [queryMapping[key]]: value } });
    //     }
    //     return acc;
    //   }, []),
    //   ...condition,
    // };
    const filter = {
      _and: [
        ...Object.entries(values).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== '') {
            acc.push({ [key]: { [queryMapping[key]]: value } });
          }
          return acc;
        }, []),
        ...(condition._and || []),
      ],
    };

    if (filter._and.length === 0) {
      delete filter._and;
    }
    try {
      setLoading(true);
      const { data: filteredPaperGroupData } = await dispatch(
        userActions.getItems('paper_groups', {
          fields: ['*'],
          limit: '-1',
          sort: 'name',
          filters: filter,
        })
      );
      if (filteredPaperGroupData.length === 0) {
        notification['warning']({
          message: 'Records not found!',
          duration: 2,
        });
      }

      const newFilteredData = filteredPaperGroupData.map(item => ({
        key: item?.id,
        name: item?.name || 'No Value',
        values: [
          { label: 'Ba Absolute Low', value: item?.ba_absolute_low },
          { label: 'Absolute Sr', value: item?.sr_absolute_low },
          { label: 'Ba/Sr Ratio Target', value: item?.ba_sr_ratio_average },
          { label: 'Ba/Sr Ratio Low Limit', value: item?.ba_sr_ratio_low_limit },
          { label: 'Ba/Sr Ratio High Limit', value: item?.ba_sr_ratio_high_limit },
          {
            label: 'Optical Brightener',
            value: OBA.find(option => option.key === item?.optical_brightener)?.value || '',
          },
          {
            label: 'Paper Sheen',
            value: PAPER_SHEEN.find(option => option.key === item?.paper_sheen)?.value || '',
          },
          {
            label: 'Paper Weight',
            value: PAPER_WEIGHT.find(option => option.key === item?.paper_weight)?.value || '',
          },
          { label: 'Paper Texture', value: PAPER_TEXTURE.find(option => option.key === item?.paper_texture)?.value || '' },
          { label: 'Se', value: '' },
          { label: 'Dmin Type', value: '' },
          {
            label: 'Date',
            value: '',
          },
          { label: 'Stamp Date', value: '' },
          { label: 'Group Stat Print Date', value: '' },
          { label: 'Name', value: item?.name ?? '' },
          { label: 'Paper Family', value: item?.paper_family ?? '' },
        ],
      }));
      setPaperGroups(newFilteredData);
    } catch (error) {
      console.log(error);
      notification['error']({
        message: 'Error occurred while searching for records!',
        duration: 2,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e, groupKey) => {
    const isChecked = e.target.checked;
    let newPaperGroup = checkedGroup == groupKey ? null : !!isChecked ? groupKey : null;
    setCheckedGroup(newPaperGroup);
    const msgData =
      groupKey == paperGroupID
        ? `Do you want to remove this paper group from the print object?`
        : `Do you want to save this paper group to the print object?`;

    Modal.info({
      title: 'Confirmation',
      className: 'custom_modal',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p> {msgData}</p>
          <div className="custom_button">
            <Button type="default" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => saveData(newPaperGroup)}>
              Save
            </Button>
          </div>
        </div>
      ),
      closable: true,
      closeIcon: <Button className="ant-modal-close-x" onClick={closeModal} type="link" icon={<CloseOutlined />} />,
    });
  };

  const closeModal = () => {
    setCheckedGroup();
    Modal.destroyAll();
  };

  const saveData = async groupID => {
    setLoading(true);
    // object status to identify its complete or non complete object
    // object type passed to idetify its print object at backend
    const dataToSave = {
      id: objectID,
      paper_group: groupID,
      object_status: props?.objectData?.basic_print_report?.[0]?.object ?? 0,
      object_type: 'print',
    };

    try {
      const response = await dispatch(userActions.saveItem('collection_obj', dataToSave));
      if (response) {
        setPaperGroupID(groupID);
        setCheckedGroup(groupID);

        const updatedPaperData = groupID
          ? await dispatch(
              userActions.getItem('paper_groups', groupID, {
                fields: ['name', 'ba_sr_ratio_low_limit', 'ba_sr_ratio_high_limit', 'paper_family.name'],
              })
            )
          : null;

        const updatedValues = groupID
          ? {
              'Ba/Sr Ratio Low Limit': updatedPaperData?.data?.ba_sr_ratio_low_limit || '',
              'Ba/Sr Ratio High Limit': updatedPaperData?.data?.ba_sr_ratio_high_limit || '',
              Name: updatedPaperData?.data?.name || '',
              'Paper Family': updatedPaperData?.data?.paper_family?.name || '',
            }
          : {
              'Ba/Sr Ratio Low Limit': '',
              'Ba/Sr Ratio High Limit': '',
              Name: '',
              'Paper Family': '',
            };

        setData(prevData =>
          prevData.map(item => ({
            ...item,
            value: updatedValues[item.label] ?? item.value,
          }))
        );

        notification.success({
          message: 'Paper group saved successfully!',
          duration: 2,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error occurred while updating the record!',
        duration: 2,
      });
    } finally {
      Modal.destroyAll();
      setLoading(false);
    }
  };

  const validateFields = () => {
    const baSrRatioAverage = formRef?.current?.getFieldValue('ba_sr_ratio_average');
    const baSrRatioAvgTargetRange = formRef?.current?.getFieldValue('ba_sr_ratio_avg_target_range');

    // Clear previous errors
    formRef.current.setFields([
      { name: 'ba_sr_ratio_average', errors: [] },
      { name: 'ba_sr_ratio_avg_target_range', errors: [] },
    ]);

    // Validation logic
    if (baSrRatioAverage && !baSrRatioAvgTargetRange) {
      formRef.current.setFields([{ name: 'ba_sr_ratio_avg_target_range', errors: ['Please fill in the +/- Avg ba/sr target.'] }]);
      return false;
    }

    if (baSrRatioAvgTargetRange && !baSrRatioAverage) {
      formRef.current.setFields([{ name: 'ba_sr_ratio_average', errors: ['Please fill in the Avg ba/sr target.'] }]);
      return false;
    }

    return true;
  };

  return (
    <Spin spinning={loading} size="large">
      <div className="dashboard">
        <Row gutter={[16, 16]} className="page-header-section" align="middle">
          <Col xs={12}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Paper group comparison view
            </Typography.Title>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="ba_absolute_low" label="Absolute ba low" rules={[{ whitespace: true }, { validator: onlyNumber }]}>
                    <Input placeholder="Absolute ba low" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="ba_absolute_high" label="Absolute ba high" rules={[{ whitespace: true }, { validator: onlyNumber }]}>
                    <Input placeholder="Absolute ba high" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="sr_absolute_low" label="Absolute sr low" rules={[{ whitespace: true }, { validator: onlyNumber }]}>
                    <Input placeholder="Absolute sr low" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="sr_absolute_high" label="Absolute sr high" rules={[{ whitespace: true }, { validator: onlyNumber }]}>
                    <Input placeholder="Absolute sr high" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item
                    name="ba_sr_ratio_low_limit"
                    label="Ba/Sr ratio low limit"
                    rules={[{ whitespace: true }, { validator: onlyNumber }]}
                  >
                    <Input placeholder="Ba/Sr ratio low limit" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item
                    name="ba_sr_ratio_high_limit"
                    label="Ba/Sr ratio high limit"
                    rules={[{ whitespace: true }, { validator: onlyNumber }]}
                  >
                    <Input placeholder="Ba/Sr ratio high limit" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item
                    name="ba_sr_ratio_average"
                    label="Avg ba/sr target"
                    dependencies={['ba_sr_ratio_avg_target_range']}
                    rules={[{ whitespace: true }, { validator: onlyNumber }]}
                  >
                    <Input placeholder="Avg ba/sr target" onKeyPress={onlyNumbers} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item
                    name="ba_sr_ratio_avg_target_range"
                    label="+/- Avg ba/sr target"
                    dependencies={['ba_sr_ratio_average']}
                    rules={[{ whitespace: true }, { validator: onlyNumber }]}
                  >
                    <Input onKeyPress={onlyNumbers} placeholder="+/- Avg ba/sr target" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="optical_brightener" label="Optical brightener">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Optical Brightener"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                      {OBA.map(col => {
                        return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="paper_sheen" label="Paper Sheen">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Paper Sheen"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                      {PAPER_SHEEN.map(col => {
                        return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="paper_weight" label="Paper Weight">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Paper Weight"
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                      {PAPER_WEIGHT.map(col => {
                        return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                {/* <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item name="dmin_sample_area" label="Factor for dmin type">
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      placeholder="Select Factor for dmin type"
                      style={{ textTransform: 'capitalize' }}
                    >
                      {dminAreaData.map(el => (
                        <Select.Option key={el.id} value={el.id}>
                          {el.dmin_sample_area}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col> */}
                <Col xs={24} sm={24} md={8} lg={8}>
                  <Form.Item>
                    <Button
                      type="primary"
                      size="small"
                      htmlType="submit"
                      style={{
                        marginTop: '28px',
                        height: '42px',
                      }}
                      icon={<SearchOutlined />}
                    >
                      Search
                    </Button>
                    <Button
                      style={{
                        marginLeft: '10px',
                        height: '42px',
                        marginTop: '28px',
                      }}
                      size="small"
                      danger
                      onClick={resetFilters}
                      icon={<ReloadOutlined />}
                    >
                      Reset Filters
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8}>
            <Card title="Print Details" bordered={true} style={{ marginTop: '12px' }}>
              <Descriptions column={1} labelStyle={{ fontWeight: 'bold', width: '200px' }}>
                {data.map((item, index) => (
                  <Descriptions.Item key={index} label={item.label}>
                    {item.value}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={16}>
            <div className="carousel-container">
              <Carousel
                draggable
                arrows={true}
                prevArrow={<LeftOutlined style={{ fontSize: '30px !important' }} />}
                nextArrow={<RightOutlined style={{ fontSize: '30px !important' }} />}
                infinite={false}
                slidesToShow={Math.min(2, paperGroups.length)}
                slidesToScroll={1}
                dots={true}
                beforeChange={handleBeforeChange}
                afterChange={current => setCurrentIndex(current)}
                adaptiveHeight={true}
                style={{ width: '100%' }}
              >
                {paperGroups.map(group => (
                  <div key={group.key} className="carousel-item">
                    <Card
                      title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            checked={checkedGroup === group.key}
                            onChange={e => handleCheckboxChange(e, group.key)}
                            style={{ pointerEvents: 'auto' }}
                            disabled={props.haveSavePermission}
                          />
                          <span style={{ marginLeft: '8px' }}>{group.name}</span>
                        </div>
                      }
                      bordered={true}
                    >
                      <Descriptions column={1}>
                        {group.values.map((item, index) => (
                          <Descriptions.Item key={index}>{item?.value || <span>&nbsp;</span>}</Descriptions.Item>
                        ))}
                      </Descriptions>
                    </Card>
                  </div>
                ))}
              </Carousel>
            </div>
          </Col>
          <Col xs={12}>
            <Button
              style={{
                margin: '0 8px',
                backgroundColor: 'lightgrey',
                color: '#333',
                borderColor: '#333',
              }}
              onClick={backButton}
            >
              Back
            </Button>
            <Button
              style={{
                margin: '0 8px',
                backgroundColor: 'lightgrey',
                color: '#333',
                borderColor: '#333',
              }}
              onClick={goToList}
            >
              Print Objects
            </Button>
          </Col>
        </Row>
      </div>
    </Spin>
  );
};
export default PaperGroup;
